import { useEffect } from "react";
import { useStaffStore } from "../../stores/staff";
import { getUsers } from "../../api/users";
import { showToastError, showToastSuccess } from "../../services/toast";
import UsersList from "./UsersList";
import { Select } from "../ui/inputs/Select";
import { getLabelsEvents } from "../../api/timeslabels";
import { Input } from "../ui/inputs/Input";
import { useButtonStore } from "../../stores/components/button";
import { getEvent, postEvent, updateEvent } from "../../api/events";
import { formatDateForInput } from "../../services/function";
import { TextArea } from "../ui/inputs/TextArea";
import { ActionButton, DetailHeader } from "../ui/page/PageShell";

const Events = () => {
  const {
    viewOrUpdate,
    formData,
    setFormData,
    setUsers,
    libelles,
    setLibelles,
    setViewOrUpdate,
    idsUsers,
    resetStaff,
    idEvent,
    updateStaff,
    setUpdateStaff,
  } = useStaffStore();
  const { setCreateButton } = useButtonStore();

  useEffect(() => {
    if (viewOrUpdate === "update") {
      getEvent(idEvent)
        .then((res) => {
          const data = res.data;
          setFormData({
            ...data,
            startDate: formatDateForInput(data.startDate),
            endDate: formatDateForInput(data.endDate),
          });
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    } else {
      getUsers(true)
        .then((res) => {
          setUsers(res.data);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  }, [viewOrUpdate, idEvent]);

  useEffect(() => {
    getLabelsEvents()
      .then((res) => {
        const formattedData = res.data.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        setLibelles(formattedData);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, description: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let data: any = formData;
    if (viewOrUpdate === "") {
      data = {
        ...formData,
        users: idsUsers,
      };
    } else {
      // Exclu les propriétés spécifiques
      const { nameTimeLabel, user, idUser, id, ...filteredData } = data;
      data = {
        ...filteredData, // Réassigne l'objet sans les propriétés exclues
      };
    }

    // Supprimer startHour et endHour si isFullDay est true
    if (data.isFullDay) {
      const { startHour, endHour, ...rest } = data;
      data = rest;
    }

    // Vérification des champs requis
    if(viewOrUpdate === "" && idsUsers.length === 0) {
      return showToastError("Veuillez sélectionner au moins un utilisateur");
    }

    {
      viewOrUpdate === ""
        ? postEvent(data)
            .then(() => {
              setCreateButton(false);
              setViewOrUpdate("");
              showToastSuccess("L'évènement a bien été créé");
              resetStaff();
              setFormData({});
              setUpdateStaff(!updateStaff);
              
            })
            .catch((err) => {
              if(err.response.data.message === "Start date is greater than end date") {
                return showToastError("La date de début est supérieure à la date de fin");
              } else if(err.response.data.message === "Chevauchement d'événements pour cette période") {
                return showToastError("Il y a un chevauchement d'événements pour cette période");
              } else {
                showToastError("Une erreur est survenue");
              }
              
            })
        : updateEvent(idEvent, data)
            .then(() => {
              setCreateButton(false);
              setViewOrUpdate("");
              showToastSuccess("L'évènement a bien été modifié");
              resetStaff();
              setFormData({});
              setUpdateStaff(!updateStaff);
            })
            .catch((err) => {
              if(err.response.data.message === "Start date is greater than end date") {
                return showToastError("La date de début est supérieure à la date de fin");
              } else if(err.response.data.message === "Chevauchement d'événements pour cette période") {
                return showToastError("Il y a un chevauchement d'événements pour cette période");
              } else {
                showToastError("Une erreur est survenue");
              }
            });
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <DetailHeader
        title={`${
          viewOrUpdate === "update" ? "Modification" : "Création"
        } d'un évènement`}
        hint={
          <>
            Merci de renseigner les évènements pour{" "}
            {viewOrUpdate !== "" ? (
              <span className="font-bold text-slate-700">{formData.user}</span>
            ) : (
              "le ou les utilisateur(s)"
            )}
            .
          </>
        }
        onClose={() => {
          setCreateButton(false);
          setViewOrUpdate("");
          setFormData({});
        }}
        actions={
          <ActionButton variant="primary" type="submit" form="eventForm">
            {viewOrUpdate !== "" ? "Modifier" : "Créer"}
          </ActionButton>
        }
      />

      <div className="flex min-h-0 w-full flex-1 items-start overflow-hidden p-3 sm:p-4">
        <form
          className="flex h-full min-h-0 w-full flex-col gap-3 overflow-hidden lg:flex-row"
          onSubmit={handleSubmit}
          id="eventForm"
        >
          {viewOrUpdate === "" && (
            <div className="h-full min-h-0 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 lg:w-1/3">
              <h4 className="mb-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                Liste des utilisateurs
              </h4>
              <UsersList />
            </div>
          )}
          <div className="h-full min-h-0 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 lg:w-2/3">
            <h4 className="mb-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Évènements
            </h4>
            <div className="w-full grid grid-cols-2 gap-4">
              <Select
                label="Motifs"
                name="idTimesLabel"
                value={formData.idTimesLabel || ""}
                onChange={handleSelectChange}
                fetchOptions={libelles}
                className="w-2/3 mt-5 col-start-1 col-end-3"
              />
              {/* Journée(s) entière(s) */}
              <div className="flex items-start col-start-1 col-end-3">
                <p className="3xl:text-md text-sm font-thin">Journée(s) entière(s):</p>
                <input type="checkbox" name="isFullDay" id="isFullDay"
                  checked={formData.isFullDay || false}
                  onChange={handleCheckboxChange}
                  className="flex mt-1 ml-2"
                />
              </div>
              <Input
                label="Date de début"
                name="startDate"
                type="date"
                required
                value={formData.startDate || ""}
                onChange={handleChange}
                clasName="w-2/3 col-start-1 col-end-2"
              />
              {!formData.isFullDay && <Input
                label="Heure de début"
                name="startHour"
                type="time"
                value={formData.startHour || ""}
                onChange={handleChange}
                required
                clasName="w-2/3 "
              />}
              
              <Input
                label="Date de fin"
                name="endDate"
                type="date"
                value={formData.endDate || ""}
                onChange={handleChange}
                required
                clasName="w-2/3 col-start-1 col-end-2"
              />
     
              {!formData.isFullDay && <Input
                label="Heure de fin"
                name="endHour"
                type="time"
                value={formData.endHour || ""}
                onChange={handleChange}
                required
                clasName="w-2/3"
              />}
              
              <TextArea
                label="Commentaire"
                name="description"
                value={formData.description || ""}
                onChange={handleTextAreaChange}
                clasName="w-full col-start-1 col-end-4"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Events;
